import React from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { isSignInWithEmailLink, signInWithEmailLink } from "firebase/auth";
import { auth } from "@/lib/firebase";
import { PageShell } from "@/components/layout/PageShell";
import { Seo } from "@/components/seo/Seo";

const STORAGE_EMAIL_KEY = "dotperson:signin-email";

export function SignInComplete() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = React.useState<
    "verifying" | "needs_email" | "completing" | "success" | "error"
  >("verifying");
  const [email, setEmail] = React.useState("");
  const [errorMsg, setErrorMsg] = React.useState("");

  const completeSignIn = React.useCallback(
    async (emailToUse: string) => {
      setStatus("completing");
      setErrorMsg("");
      try {
        await signInWithEmailLink(auth, emailToUse, window.location.href);
        window.localStorage.removeItem(STORAGE_EMAIL_KEY);

        // Bridge: provision users/{uid} from contributors lookup if first sign-in.
        const user = auth.currentUser;
        if (user) {
          const idToken = await user.getIdToken();
          await fetch("/api/admin/voices/bridge-session", {
            method: "POST",
            headers: { Authorization: `Bearer ${idToken}` },
          });
        }

        setStatus("success");
        const redirect = params.get("redirect") || "/admin/voices";
        setTimeout(() => navigate(redirect, { replace: true }), 600);
      } catch (err) {
        setErrorMsg(`Could not complete sign-in: ${String(err)}`);
        setStatus("error");
      }
    },
    [navigate, params]
  );

  React.useEffect(() => {
    if (!isSignInWithEmailLink(auth, window.location.href)) {
      setStatus("error");
      setErrorMsg(
        "This page only completes a magic-link sign-in. The link in your URL is not a valid Firebase email-link credential."
      );
      return;
    }
    const stored = window.localStorage.getItem(STORAGE_EMAIL_KEY);
    if (stored) {
      void completeSignIn(stored);
    } else {
      setStatus("needs_email");
    }
  }, [completeSignIn]);

  return (
    <PageShell title="Admin · Completing sign-in" status="restricted">
      <Seo
        title="Completing sign-in — The .person Foundation admin"
        description="Magic-link sign-in completion."
        path="/admin/sign-in/complete"
        noindex
      />
      <div className="max-w-md mx-auto py-12">
        {status === "verifying" && (
          <p className="font-mono text-xs text-ink-3 uppercase tracking-widest">
            Verifying link…
          </p>
        )}

        {status === "needs_email" && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (email) void completeSignIn(email.trim().toLowerCase());
            }}
            className="space-y-6"
          >
            <p className="font-serif text-base text-ink-2 leading-relaxed">
              We need to confirm the email this magic link was sent to. Re-enter
              your address to complete sign-in.
            </p>
            <div>
              <label
                htmlFor="confirm-email"
                className="block font-mono text-[10px] text-ink-3 uppercase tracking-widest mb-2"
              >
                Email
              </label>
              <input
                id="confirm-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-paper border border-ink-3/30 px-4 py-3 font-mono text-sm text-ink focus:outline-none focus:border-ink"
                required
              />
            </div>
            <button
              type="submit"
              className="w-full bg-ink text-paper font-mono text-xs uppercase tracking-widest py-3"
            >
              Complete sign-in
            </button>
          </form>
        )}

        {status === "completing" && (
          <p className="font-mono text-xs text-ink-3 uppercase tracking-widest">
            Completing…
          </p>
        )}

        {status === "success" && (
          <p className="font-mono text-xs text-emerald-600 uppercase tracking-widest">
            Signed in. Redirecting…
          </p>
        )}

        {status === "error" && (
          <div className="space-y-4">
            <div className="border border-red-500/40 bg-red-500/5 px-4 py-3 font-mono text-xs text-red-500">
              {errorMsg}
            </div>
            <Link
              to="/admin/sign-in"
              className="font-mono text-xs underline text-ink-2"
            >
              Request a new sign-in link
            </Link>
          </div>
        )}
      </div>
    </PageShell>
  );
}
